import React, { useState, useRef, useEffect } from 'react';
import { Formik } from 'formik';
import { useDebounce } from 'react-use';
import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Button from 'src/components/ui/Button';
import Divider from 'src/components/ui/Divider';
import Typography from 'src/components/ui/Typography';
import SimpleHorizontalUserCard from 'src/components/ui/SimpleHorizontalUserCard';
import Icon from 'src/components/ui/Icon';
import Loader from 'src/components/ui/Loader';
import { Tab, Tabs } from 'src/components/ui/Tabs';

import useAlerts from 'src/hooks/useAlerts';
import useUser from 'src/hooks/useUser';

import { useRecommendingUsersQuery } from 'src/hooks/__generated__/queries';
import type {
    Recommendation,
    UserProfileQuery,
    User,
    RecommendingUsersQuery,
    CompanyProfileQuery,
} from 'src/types/__generated__/graphql';
import { RecommendationStatus, UserType } from 'src/types/__generated__/graphql';
import { colours } from 'src/constants/colours';
import { isBottomDiv } from 'src/utils/isBottom';
import useLoginModal from 'src/hooks/useLoginModal';
import TextField from 'src/components/ui/TextField';
import { styledComponent } from 'src/utils/styled';

const StyledTabs = styledComponent(Tabs)`
    box-sizing: border-box;
    margin-top: 1rem;
`;

const TextFieldWrapper = styledComponent(Box)`
    margin-left: -1.5rem;
    width: calc(100% + 3rem);
    padding: 0 1.5rem;
    box-shadow: 0 0.625rem 1.25rem ${colours.blackOpacity.O08};
    z-index: 10;
`;

const StyledRecommendationBox = styledComponent(Box)`
    border-top: 0.0625rem solid ${colours.grey.G200};
    box-shadow: 0 0.5rem 2.5rem rgb(19 46 72 / 16%);
    margin: 0 -1.5rem;
    background-color: ${colours.white.main};
`;

interface Props {
    user?: NonNullable<UserProfileQuery['userProfile']>;
    company?: NonNullable<CompanyProfileQuery['companyProfile']>;
    recommendationsCount: number;
    handleNext: () => void;
    recommendationFromMe?: Pick<Recommendation, 'id' | 'status'> | null;
    type: 'list' | 'search';
}

const defaultTake = 10;
const RecommendationsModalContent: React.FC<Props> = ({
    user,
    company,
    recommendationsCount,
    handleNext,
    recommendationFromMe,
    type,
}) => {
    const { t } = useTranslation(['profile', 'common']);
    const { addAlert } = useAlerts();
    const currentUser = useUser();
    const [selectedTab, setSelectedTab] = useState<UserType>(UserType.Pro);
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const scrollerContainerRef = useRef<HTMLDivElement | null>(null);

    const { createAuthenticatedHandler } = useLoginModal();

    const {
        data: proRecommendingUsers,
        fetchMore: fetchMoreProRecommendingUsers,
        loading: loadingProRecommendingUsers,
    } = useRecommendingUsersQuery({
        variables: {
            offset: 0,
            limit: defaultTake,
            userId: user?.id,
            companyId: company?.id,
            userType: UserType.Pro,
        },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        skip: (!user?.id && !company?.id) || type === 'search',
    });

    const {
        data: individualsRecommendingUsers,
        fetchMore: fetchMoreIndividualsRecommendingUsers,
        loading: loadingIndividualsRecommendingUsers,
    } = useRecommendingUsersQuery({
        variables: {
            offset: 0,
            limit: defaultTake,
            userId: user?.id,
            companyId: company?.id,
            userType: UserType.NonPro,
        },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        skip: (!user?.id && !company?.id) || type === 'search',
    });

    const {
        data: searchRecommendingUsers,
        fetchMore: fetchMoreSearchRecommendingUsers,
        loading: loadingSearchRecommendingUsers,
    } = useRecommendingUsersQuery({
        variables: {
            offset: 0,
            limit: defaultTake,
            userId: user?.id,
            companyId: company?.id,
            userType: UserType.All,
            query: debouncedQuery.length > 0 ? debouncedQuery : null,
        },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        skip: (!user?.id && !company?.id) || type === 'list',
    });

    const allDataLoaded = {
        pro: Boolean(
            proRecommendingUsers?.recommendingUsers?.results &&
                proRecommendingUsers.recommendingUsers.total &&
                proRecommendingUsers.recommendingUsers.results.length >= proRecommendingUsers.recommendingUsers.total,
        ),
        individuals: Boolean(
            individualsRecommendingUsers?.recommendingUsers?.results &&
                individualsRecommendingUsers.recommendingUsers.total &&
                individualsRecommendingUsers.recommendingUsers.results.length >=
                    individualsRecommendingUsers.recommendingUsers.total,
        ),
        search: Boolean(
            searchRecommendingUsers?.recommendingUsers?.results &&
                searchRecommendingUsers.recommendingUsers.total &&
                searchRecommendingUsers.recommendingUsers.results.length >=
                    searchRecommendingUsers.recommendingUsers.total,
        ),
    };

    useDebounce(
        () => {
            setDebouncedQuery(query);
            if (scrollerContainerRef.current) {
                scrollerContainerRef.current.scrollTop = 0;
            }
        },
        500,
        [query],
    );

    useEffect(() => {
        if (scrollerContainerRef.current) {
            scrollerContainerRef.current.scrollTop = 0;
        }
    }, [type]);

    let recommendingUsers: RecommendingUsersQuery['recommendingUsers'] | undefined;
    if (type === 'list') {
        recommendingUsers = (selectedTab === UserType.Pro ? proRecommendingUsers : individualsRecommendingUsers)
            ?.recommendingUsers;
    } else {
        recommendingUsers = searchRecommendingUsers?.recommendingUsers;
    }

    const isLoading =
        loadingProRecommendingUsers || loadingIndividualsRecommendingUsers || loadingSearchRecommendingUsers;

    const handleScroll = async (ev: React.UIEvent<HTMLDivElement, UIEvent>) => {
        const bottom = isBottomDiv(ev.currentTarget);
        if (bottom && !isLoading) {
            try {
                if (type === 'list') {
                    if (selectedTab === UserType.Pro && !allDataLoaded.pro) {
                        const proActualDataLength = proRecommendingUsers?.recommendingUsers?.results?.length ?? 0;
                        await fetchMoreProRecommendingUsers({
                            variables: {
                                offset: proActualDataLength,
                            },
                        });
                    } else if (selectedTab === UserType.NonPro && !allDataLoaded.individuals) {
                        const individualsActualDataLength =
                            individualsRecommendingUsers?.recommendingUsers?.results?.length ?? 0;
                        await fetchMoreIndividualsRecommendingUsers({
                            variables: {
                                offset: individualsActualDataLength,
                            },
                        });
                    }
                } else if (!allDataLoaded.search) {
                    const searchActualDataLength = searchRecommendingUsers?.recommendingUsers?.results?.length ?? 0;
                    await fetchMoreSearchRecommendingUsers({
                        variables: {
                            offset: searchActualDataLength,
                        },
                    });
                }
            } catch (error: unknown) {
                addAlert({
                    type: 'error',
                    message: (error as Error).message,
                });
            }
        }
    };

    const handleTabChange = (_ev: React.SyntheticEvent, tab: UserType) => {
        if (scrollerContainerRef.current) {
            scrollerContainerRef.current.scrollTop = 0;
        }
        setSelectedTab(tab);
    };

    const isMyProfile = currentUser && user?.id === currentUser.id;
    const iAlreadyRecommended = recommendationFromMe?.status === RecommendationStatus.Accepted;

    return (
        <>
            {recommendationsCount === 0 ? (
                <Box
                    display="flex"
                    flexDirection="column"
                    height="20rem"
                    justifyContent="center"
                    alignItems="center"
                    flex={1}
                >
                    <Icon color={colours.primary.main} size="7.5rem" name="BigThumbsUp" />
                    <Box maxWidth="10rem" mt={2}>
                        <Typography variant="body2" align="center">
                            {t('profile:recommendationsEmptyState', {
                                fullName: user?.fullName ?? company?.name,
                            })}
                        </Typography>
                    </Box>
                </Box>
            ) : (
                <Box
                    mb={isMyProfile || type === 'search' ? '1.5rem' : 0}
                    display="flex"
                    flexDirection="column"
                    flex={1}
                >
                    {type === 'list' ? (
                        <StyledTabs value={selectedTab} onChange={handleTabChange} variant="fullWidth">
                            <Tab
                                label={t('common:professionals', {
                                    count: proRecommendingUsers?.recommendingUsers?.total ?? 0,
                                })}
                                value={UserType.Pro}
                            />
                            <Tab
                                label={t('common:individuals', {
                                    count: individualsRecommendingUsers?.recommendingUsers?.total ?? 0,
                                })}
                                value={UserType.NonPro}
                            />
                        </StyledTabs>
                    ) : (
                        <Formik initialValues={{ query: '' }} onSubmit={console.info}>
                            {/* Formik is used here to avoid errors using useField in children components */}
                            <TextFieldWrapper>
                                <TextField
                                    data-target-id="search-profile-recommendations-modal"
                                    name="search"
                                    label={t('common:search')}
                                    InputProps={{
                                        startAdornment: <Icon name="Search" />,
                                    }}
                                    onChange={(e) => setQuery(e.target.value)}
                                />
                            </TextFieldWrapper>
                        </Formik>
                    )}
                    <Box mx={-3}>
                        <Divider />
                    </Box>
                    <Box
                        ref={scrollerContainerRef}
                        display="flex"
                        flexDirection="column"
                        height="20rem"
                        mx={-1}
                        overflow="auto"
                        onScroll={handleScroll}
                    >
                        {recommendingUsers?.results?.map((_user) => (
                            <div key={_user?.id}>
                                <SimpleHorizontalUserCard
                                    user={_user as User}
                                    firstSubtitle={
                                        selectedTab === UserType.NonPro && type === 'list'
                                            ? null // send null to not display activity in this case
                                            : undefined
                                    }
                                    secondSubtitle={
                                        _user?.id === currentUser?.id
                                            ? undefined // send undefined to not display common relations for current user
                                            : 'commonRelationsCount'
                                    }
                                    minHeight="5rem"
                                    padding="0.5rem 1rem"
                                    backgroundColor="transparent"
                                />
                            </div>
                        ))}
                        {isLoading ? <Loader /> : null}
                    </Box>
                </Box>
            )}
            {!isMyProfile && type === 'list' ? (
                <StyledRecommendationBox>
                    <Box display="flex" padding="1rem 0" width="100%">
                        <Button
                            sx={{ margin: '0 1.5rem' }}
                            startIcon={<Icon name={iAlreadyRecommended ? 'ThumbsUpCrossed' : 'ThumbsUpOutlined'} />}
                            onClick={
                                process.env.NODE_ENV === 'test'
                                    ? handleNext
                                    : () => createAuthenticatedHandler(handleNext)
                            }
                            variant={iAlreadyRecommended ? 'outlined' : undefined}
                            fullWidth
                        >
                            {t(iAlreadyRecommended ? 'profile:removeRecommendation' : 'profile:recommend')}
                        </Button>
                    </Box>
                </StyledRecommendationBox>
            ) : null}
        </>
    );
};

export default RecommendationsModalContent;
